/**
 * Local Oshar VR Real Estate UI content (districts, hero copy, static fallback tiers).
 * Canonical tier definitions and live inventory: TreydoraEdBackend `osharRealEstateTierCatalog.js`
 * and GET /api/oshar-real-estate/tiers.
 */

export const districts = [
  {
    id: "central-spire",
    name: "Central Spire",
    type: "Legendary civic core",
    startingPrice: "From $1,499",
    bestFor: "Prestige identity, civic landmarks, skyline presence",
    propertyTypes: ["High-End Estates", "Guild Halls", "Arenas / Event Spaces"],
    previewKey: "palaceNight",
  },
  {
    id: "commerce-ward",
    name: "Commerce Ward",
    type: "Merchant & trade hub",
    startingPrice: "From $299",
    bestFor: "Approved digital goods, storefronts, education products",
    propertyTypes: ["Commercial Storefronts", "Vendor Stalls"],
    previewKey: "commerceMarket",
  },
  {
    id: "waterfront",
    name: "Waterfront District",
    type: "Canal & harbor commerce",
    startingPrice: "From $149",
    bestFor: "Boutique presence, views, social commerce",
    propertyTypes: ["Vendor Stalls", "Mid-Tier Residences", "Commercial Storefronts"],
    previewKey: "waterfrontVenice",
  },
  {
    id: "residential-ring",
    name: "Residential Ring",
    type: "Citizenship & housing band",
    startingPrice: "From $49",
    bestFor: "Entry ownership, Oshar citizenship layer, community",
    propertyTypes: ["Entry-Level Residences", "Mid-Tier Residences"],
    previewKey: "aerialEstateRing",
  },
  {
    id: "guild-quarter",
    name: "Guild Quarter",
    type: "Alliance headquarters",
    startingPrice: "From $9,999",
    bestFor: "Guild identity, private events, alliance space",
    propertyTypes: ["Guild Halls", "High-End Estates"],
    previewKey: "councilGuild",
  },
  {
    id: "arena-circle",
    name: "Arena Circle",
    type: "Tournament & spectacle",
    startingPrice: "From $29,999",
    bestFor: "Coliseum events, showcases, limited experiences",
    propertyTypes: ["Arenas / Event Spaces"],
    previewKey: "arenaColiseum",
  },
  {
    id: "artisan-forge",
    name: "Artisan Forge District",
    type: "Craft & industry",
    startingPrice: "From $4,999",
    bestFor: "Weapon, armor, and equipment-linked production",
    propertyTypes: ["Forges / Workshops", "Vendor Stalls"],
    previewKey: "luxuryStorefront",
  },
  {
    id: "resource-district",
    name: "Resource District",
    type: "Production & supply",
    startingPrice: "From $4,999",
    bestFor: "Farming goods, resource identity, rare nodes",
    propertyTypes: ["Farms", "Alchemy Labs"],
    previewKey: "mapTopDown",
  },
];

const assetTypesBase = [
  {
    id: "commercial-storefronts",
    name: "Commercial Storefronts",
    supply: 500,
    price: 2499,
    priceLabel: "$2,499",
    useCase:
      "Sell approved digital goods, skins, maps, weapons, armor, services, education products, NFT comic merch, and Treydora-related goods.",
    imageKey: "commerceMarket",
  },
  {
    id: "vendor-stalls",
    name: "Vendor Stalls",
    supply: 200,
    price: 299,
    priceLabel: "$299",
    useCase: "Smaller merchant presence for approved marketplace sales.",
    imageKey: "vendorStallStorefront",
  },
  {
    id: "high-end-estates",
    name: "High-End Estates",
    supply: 300,
    price: 1499,
    priceLabel: "$1,499",
    useCase: "Premium residence identity, private space, prestige, and game-linked recognition.",
    imageKey: "palaceNight",
  },
  {
    id: "mid-tier-residences",
    name: "Mid-Tier Residences",
    supply: 10000,
    price: 149,
    priceLabel: "$149",
    useCase: "Standard residence access and Oshar citizenship layer.",
    imageKey: "aerialEstateRing",
  },
  {
    id: "entry-residences",
    name: "Entry-Level Residences",
    supply: 20000,
    price: 49,
    priceLabel: "$49",
    useCase: "Affordable entry into Oshar ownership and identity.",
    imageKey: "waterfrontVenice",
  },
  {
    id: "farms",
    name: "Farms",
    supply: 15,
    price: 4999,
    priceLabel: "$4,999",
    useCase: "Rare production node for farming goods and resource identity.",
    imageKey: "farmBioluminescent",
  },
  {
    id: "forges",
    name: "Forges / Workshops",
    supply: 15,
    price: 4999,
    priceLabel: "$4,999",
    useCase: "Rare crafting and production node for weapon, armor, and equipment-related utility.",
    imageKey: "councilGuild",
  },
  {
    id: "alchemy-labs",
    name: "Alchemy Labs",
    supply: 15,
    price: 0,
    priceLabel: "TBD",
    useCase: "Rare production node for potions, recipes, and mystical crafting.",
    imageKey: "commerceMarket",
  },
  {
    id: "guild-halls",
    name: "Guild Halls",
    supply: 20,
    price: 9999,
    priceLabel: "$9,999",
    useCase: "Guild headquarters, alliance identity, group events, and private community space.",
    imageKey: "councilGuild",
  },
  {
    id: "arenas",
    name: "Arenas / Event Spaces",
    supply: 5,
    price: 29999,
    priceLabel: "$29,999",
    useCase: "Coliseum events, tournaments, showcases, and special experiences.",
    imageKey: "arenaColiseum",
  },
];

const assetMeta = {
  "commercial-storefronts": {
    tokenId: 1,
    district: "Commerce Ward",
    assetType: "Commercial Storefront",
    saleTierLabel: "Genesis Commerce Tier",
    shortDescription: "Prime storefront lanes built for approved commerce and premium district visibility.",
    description:
      "A flagship merchant parcel positioned for high-traffic exposure and persistent storefront branding across the Oshar economy.",
    status: "Available",
    usdcPrice: 2499,
    utilityList: ["Persistent storefront signage rights", "Merchant dashboard analytics lane", "District-level commerce access"],
  },
  "vendor-stalls": {
    tokenId: 2,
    district: "Commerce Ward",
    assetType: "Vendor Stall",
    saleTierLabel: "Genesis Merchant Tier",
    shortDescription: "Flexible merchant footprint for rotating launches and marketplace events.",
    description: "A compact but strategic stall placement for high-frequency listings and community-driven drops.",
    status: "Available",
    usdcPrice: 299,
    utilityList: ["Vendor lane marketplace access", "Rotating showcase events", "Brand kiosk customization"],
  },
  "high-end-estates": {
    tokenId: 3,
    district: "Central Spire",
    assetType: "High-End Estate",
    saleTierLabel: "Genesis Prestige Tier",
    shortDescription: "Prestige estate parcels for long-term identity and skyline-level presence.",
    description: "A premium estate class parcel engineered for status, social signaling, and private residential utility.",
    statusOverride: "reserved",
    reservedBy: "Treydora Concierge Hold",
    reservationNote: "This property is currently reserved.",
    usdcPrice: 1499,
    utilityList: ["Private estate instancing", "Prestige profile enhancement", "Access to elite district events"],
  },
  "mid-tier-residences": {
    tokenId: 4,
    district: "Residential Ring",
    assetType: "Mid-Tier Residence",
    saleTierLabel: "Genesis Citizen Tier",
    shortDescription: "Core residency parcels for community identity and progression in Oshar.",
    description: "Mid-tier residential ownership supporting citizen identity, social participation, and city progression.",
    status: "Available",
    usdcPrice: 149,
    utilityList: ["Residency identity marker", "Community district access", "Citizen progression eligibility"],
  },
  "entry-residences": {
    tokenId: 5,
    district: "Residential Ring",
    assetType: "Entry-Level Residence",
    saleTierLabel: "Genesis Entry Tier",
    shortDescription: "Accessible ownership path into Oshar residency and district participation.",
    description: "An on-ramp parcel for early participation, identity anchoring, and neighborhood-level presence.",
    status: "Available",
    usdcPrice: 49,
    utilityList: ["Entry ownership credential", "District social access", "Upgradeable ownership path"],
  },
  "farms": {
    tokenId: 6,
    district: "Resource District",
    assetType: "Farm",
    saleTierLabel: "Genesis Resource Tier",
    shortDescription: "Rare production class parcel powering supply-chain narratives and resource identity.",
    description: "Scarce farm parcels tied to production utility and strategic guild-level resource positioning.",
    statusOverride: null,
    usdcPrice: 4999,
    utilityList: ["Resource production identity", "Guild trade leverage", "High-scarcity parcel class"],
  },
  "forges": {
    tokenId: 7,
    district: "Artisan Forge District",
    assetType: "Forge / Workshop",
    saleTierLabel: "Genesis Artisan Tier",
    shortDescription: "Rare craft-production nodes for weapon, armor, and equipment ecosystem roles.",
    description: "Industrial-grade forging parcels designed for elite crafting narratives and workshop prestige.",
    status: "Available",
    usdcPrice: 4999,
    utilityList: ["Workshop-brand territory", "Craft economy positioning", "Premium artisan district presence"],
  },
  "alchemy-labs": {
    tokenId: 8,
    district: "Resource District",
    assetType: "Alchemy Lab",
    saleTierLabel: "Genesis Arcane Tier",
    shortDescription: "Mystical production spaces for potion-line identity and arcane economic roles.",
    description: "Rare alchemy parcels optimized for high-prestige mystical crafting identity and role specialization.",
    status: "Available",
    usdcPrice: null,
    utilityList: ["Arcane craft identity", "Potion production narrative rights", "Specialized role progression anchor"],
  },
  "guild-halls": {
    tokenId: 9,
    district: "Guild Quarter",
    assetType: "Guild Hall",
    saleTierLabel: "Genesis Alliance Tier",
    shortDescription: "Alliance headquarters parcels for coordinated operations and private member rituals.",
    description: "High-command guild infrastructure designed for coordinated alliances and private strategic events.",
    statusOverride: "reserved",
    reservedBy: "Guild Allocation Hold",
    reservationNote: "This property is currently reserved.",
    usdcPrice: 9999,
    utilityList: ["Guild command headquarters", "Alliance gathering venue", "Private event orchestration"],
  },
  arenas: {
    tokenId: 10,
    district: "Arena Circle",
    assetType: "Arena / Event Space",
    saleTierLabel: "Genesis Spectacle Tier",
    shortDescription: "Ultra-limited spectacle asset class for event operators and tournament hosts.",
    description: "An iconic event parcel designed for flagship competitions, showcases, and high-visibility moments.",
    statusOverride: "unavailable",
    reservationNote: "Arena inventory is temporarily unavailable.",
    usdcPrice: 29999,
    utilityList: ["Tournament hosting rights", "High-impact event staging", "Flagship district visibility"],
  },
};

export const assetTypes = assetTypesBase.map((asset) => ({
  ...asset,
  ...assetMeta[asset.id],
}));

export const genesisPhaseOne = {
  title: "GENESIS DISTRICT FIRST RELEASE",
  totalAssets: 10000,
  lines: [
    { label: "Commercial storefronts", count: 100 },
    { label: "Vendor stalls", count: 50 },
    { label: "High-end estates", count: 100 },
    { label: "Mid-tier residences", count: 3000 },
    { label: "Entry-level residences", count: 6724 },
    { label: "Farms", count: 5 },
    { label: "Forges / workshops", count: 5 },
    { label: "Alchemy labs", count: 5 },
    { label: "Guild halls", count: 10 },
    { label: "Arena / event space", count: 1 },
  ],
  body:
    "The first release opens one major district instead of flooding the entire city supply. The goal is to launch with citizens, merchants, rare production assets, guild presence, and one major arena/event space.",
};

export const featuredListings = [
  {
    id: "skyspire",
    tierSlug: "high-end-estates",
    tokenId: 3,
    supply: 300,
    price: 1499,
    usdcPrice: 1499,
    maxPerCheckout: 50,
    saleTierLabel: "Genesis Prestige Tier",
    shortDescription:
      "Prestige estate parcels for long-term identity and skyline-level presence.",
    description:
      "A premium estate class parcel engineered for status, social signaling, and private residential utility.",
    title: "Skyspire Estate",
    district: "Central Spire",
    assetType: "High-End Estate",
    priceLabel: "$12,400",
    status: "Limited",
    imageKey: "palaceNight",
    statusOverride: "reserved",
    reservationNote: "This property is currently reserved.",
  },
  {
    id: "merchant-plaza",
    tierSlug: "commercial-storefronts",
    tokenId: 1,
    supply: 500,
    price: 2499,
    usdcPrice: 2499,
    maxPerCheckout: 50,
    saleTierLabel: "Genesis Commerce Tier",
    shortDescription:
      "Prime storefront lanes built for approved commerce and premium district visibility.",
    description:
      "A flagship merchant parcel positioned for high-traffic exposure and persistent storefront branding across the Oshar economy.",
    utilityList: [
      "Persistent storefront signage rights",
      "Merchant dashboard analytics lane",
      "District-level commerce access",
    ],
    title: "Merchant Plaza",
    district: "Commerce Ward",
    assetType: "Commercial Storefront",
    priceLabel: "$2,499",
    status: "Available",
    imageKey: "commerceMarket",
  },
  {
    id: "waterfront-villa",
    tierSlug: "mid-tier-residences",
    tokenId: 4,
    supply: 10000,
    price: 149,
    usdcPrice: 149,
    maxPerCheckout: 50,
    saleTierLabel: "Genesis Citizen Tier",
    shortDescription: "Core residency parcels for community identity and progression in Oshar.",
    description:
      "Mid-tier residential ownership supporting citizen identity, social participation, and city progression.",
    utilityList: ["Residency identity marker", "Community district access", "Citizen progression eligibility"],
    title: "Waterfront Villa",
    district: "Waterfront District",
    assetType: "Mid-Tier Residence",
    priceLabel: "$149",
    status: "Available",
    imageKey: "waterfrontVenice",
  },
  {
    id: "arcane-forge",
    tierSlug: "forges",
    tokenId: 7,
    supply: 15,
    price: 4999,
    usdcPrice: 4999,
    maxPerCheckout: 15,
    saleTierLabel: "Genesis Artisan Tier",
    shortDescription:
      "Rare craft-production nodes for weapon, armor, and equipment ecosystem roles.",
    description:
      "Industrial-grade forging parcels designed for elite crafting narratives and workshop prestige.",
    utilityList: ["Workshop-brand territory", "Craft economy positioning", "Premium artisan district presence"],
    title: "Arcane Forge",
    district: "Artisan Forge District",
    assetType: "Forge / Workshop",
    priceLabel: "$4,999",
    status: "Limited",
    imageKey: "councilGuild",
  },
  {
    id: "guild-hall-oshar",
    tierSlug: "guild-halls",
    tokenId: 9,
    supply: 20,
    price: 9999,
    usdcPrice: 9999,
    maxPerCheckout: 20,
    saleTierLabel: "Genesis Alliance Tier",
    shortDescription:
      "Alliance headquarters parcels for coordinated operations and private member rituals.",
    description:
      "High-command guild infrastructure designed for coordinated alliances and private strategic events.",
    utilityList: ["Guild command headquarters", "Alliance gathering venue", "Private event orchestration"],
    title: "Guild Hall of Oshar",
    district: "Guild Quarter",
    assetType: "Guild Hall",
    priceLabel: "$9,999",
    status: "Coming Soon",
    imageKey: "councilGuild",
    statusOverride: "reserved",
    reservationNote: "This property is currently reserved.",
  },
  {
    id: "arena-prime",
    tierSlug: "arenas",
    tokenId: 10,
    supply: 5,
    price: 29999,
    usdcPrice: 29999,
    maxPerCheckout: 5,
    saleTierLabel: "Genesis Spectacle Tier",
    shortDescription:
      "Ultra-limited spectacle asset class for event operators and tournament hosts.",
    description:
      "An iconic event parcel designed for flagship competitions, showcases, and high-visibility moments.",
    utilityList: ["Tournament hosting rights", "High-impact event staging", "Flagship district visibility"],
    title: "Arena Prime",
    district: "Arena Circle",
    assetType: "Arena / Event Space",
    priceLabel: "$29,999",
    status: "Coming Soon",
    imageKey: "arenaColiseum",
    statusOverride: "unavailable",
    reservationNote: "Arena inventory is temporarily unavailable.",
  },
];

export const whyOwnBenefits = [
  "VR identity tied to your parcel",
  "Property dashboard access",
  "Storefront and merchant tools",
  "Marketplace participation",
  "Guild presence and alliance space",
  "Event hosting where supported",
  "Approved game-linked utility",
  "Connection to the Treydora RPG ecosystem",
];

export const howItWorksSteps = [
  {
    n: 1,
    title: "Connect Wallet or Create Account",
    body: "Link a supported wallet or register so purchases can be attributed to you.",
  },
  {
    n: 2,
    title: "Choose District",
    body: "Explore the circular city map and pick the ward that fits your role.",
  },
  {
    n: 3,
    title: "Select Property",
    body: "Compare asset types, supply, and utility before you commit.",
  },
  {
    n: 4,
    title: "Complete Purchase",
    body: "Checkout flows will support card and wallet paths when integrated.",
  },
  {
    n: 5,
    title: "Receive NFT + Unlock Dashboard",
    body: "On-chain delivery and dashboard unlocks follow successful minting.",
  },
];
